/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useRef, useState } from 'react';

import { SharedValue } from 'react-native-reanimated';

type EpgHandlerProps = {
    dataClass: string;
    contentWidth?: number;
    offsetX?: SharedValue<number>;
    offsetY?: SharedValue<number>;
    isVertical?: boolean;
    isHorizontal?: boolean;
};

enum EDragDirection {
    horizontal,
    vertical,
}

const PROGRAM_LIST_ITEM_ID = 'PROGRAM_LIST_ITEM_ID';

function useEpgDragHandlers({
    dataClass,
    contentWidth,
    offsetX,
    offsetY,
    isVertical,
    isHorizontal,
}: EpgHandlerProps) {
    const [oldX, setOldX] = useState(0);
    const [oldY, setOldY] = useState(0);
    const isDraggedRef = useRef(false);
    const isDiagonalRef = useRef(false);
    const pointerIdRef = useRef(undefined);
    const dragDirectionRef = useRef<EDragDirection | undefined>(undefined);
    const isEpgGrid = dataClass.includes('Grid');

    const handleToggleDragEvent = useCallback((event) => {
        const isMouseDownEvent = event.type === 'mousedown';
        const isMouseUpEvent = event.type === 'mouseup';
        const isMouseClickedEvent = event.type === 'click';
        const newXValue = isMouseDownEvent ? event.pageX : 0;
        const newYValue = isMouseDownEvent ? event.pageY : 0;
        const programListTarget =
            event.target.dataset.testid?.includes(PROGRAM_LIST_ITEM_ID) ||
            event.target.parentElement?.dataset.testid?.includes(
                PROGRAM_LIST_ITEM_ID,
            ) ||
            event.target.parentElement?.parentElement?.dataset.testid?.includes(
                PROGRAM_LIST_ITEM_ID,
            ) ||
            event.target.parentElement?.parentElement?.parentElement?.dataset.testid?.includes(
                PROGRAM_LIST_ITEM_ID,
            );

        if ((isMouseClickedEvent || isMouseUpEvent) && isDraggedRef.current) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            pointerIdRef.current = undefined;
            isDiagonalRef.current = false;
            dragDirectionRef.current = undefined;
            setOldX(0);
            setOldY(0);

            return;
        }

        setOldX(newXValue);
        setOldY(newYValue);
        isDiagonalRef.current = false;

        if (
            (event.type === 'mouseleave' ||
                isMouseUpEvent ||
                (!programListTarget && isEpgGrid)) &&
            isDraggedRef.current
        ) {
            pointerIdRef.current = undefined;
            isDraggedRef.current = false;
            isDiagonalRef.current = false;
            dragDirectionRef.current = undefined;
            setOldX(0);
            setOldY(0);
        }
    }, []);

    const detectDragDirection = useCallback(
        (newX, newY) => {
            if (
                (Math.abs(oldX - newX) > 10 && Math.abs(oldY - newY) > 10) ||
                Math.abs(oldY - newY) === Math.abs(oldX - newX)
            ) {
                isDraggedRef.current = true;

                return '';
            }

            if (Math.abs(oldY - newY) - Math.abs(oldX - newX) > 2) {
                return EDragDirection.vertical;
            }

            if (Math.abs(oldX - newX) - Math.abs(oldY - newY) > 2) {
                return EDragDirection.horizontal;
            }

            return '';
        },
        [oldX, oldY],
    );

    const handleDragEvent = useCallback(
        (event) => {
            if (event.movementX === 0 && event.movementY === 0) {
                pointerIdRef.current = undefined;
                isDraggedRef.current = false;
                return;
            }

            if (
                isHorizontal &&
                isVertical &&
                offsetX &&
                oldX &&
                offsetY &&
                oldY &&
                !event.target.dataset?.testid
            ) {
                const dragDirection = detectDragDirection(
                    event.pageX,
                    event.pageY,
                );

                if (
                    typeof dragDirectionRef.current === 'number' &&
                    typeof dragDirection === 'number' &&
                    dragDirection !== dragDirectionRef.current
                ) {
                    dragDirectionRef.current = undefined;
                    setOldX(0);
                    setOldY(0);

                    return;
                }

                event.preventDefault();
                isDraggedRef.current = true;

                const draggableElements = document.querySelectorAll(
                    `div[data-class="${dataClass}"]`,
                );
                const draggableElement =
                    draggableElements?.[draggableElements?.length - 1];

                if (
                    !draggableElement.classList.contains('epgContainerDisabled')
                ) {
                    draggableElement.classList.add('epgContainerDisabled');
                }

                if (
                    dragDirection === EDragDirection.horizontal &&
                    !isDiagonalRef.current
                ) {
                    if (
                        offsetX.value - event.movementX * 2 <
                        (contentWidth ?? 0) - draggableElement.scrollWidth
                    ) {
                        offsetX.value -= event.movementX * 2;
                    } else {
                        offsetX.value =
                            (contentWidth ?? 0) - draggableElement.scrollWidth;
                    }

                    dragDirectionRef.current = dragDirection;
                } else if (
                    dragDirection === EDragDirection.vertical &&
                    !isDiagonalRef.current
                ) {
                    offsetY.value -= event.movementY;
                    dragDirectionRef.current = dragDirection;
                }

                setOldX(event.pageX);
                setOldY(event.pageY);
            } else if (isHorizontal && offsetX && oldX) {
                event.preventDefault();
                isDraggedRef.current = true;

                offsetX.value -= event.movementX;
            } else if (isVertical && offsetY && oldY) {
                event.preventDefault();
                isDraggedRef.current = true;

                offsetY.value -= event.movementY;
            }
        },
        [
            oldX,
            oldY,
            offsetX,
            offsetY,
            isHorizontal,
            isVertical,
            detectDragDirection,
            dataClass,
        ],
    );

    useEffect(() => {
        const draggableElements = document.querySelectorAll(
            `div[data-class="${dataClass}"]`,
        );
        const draggableElement =
            draggableElements?.[draggableElements?.length - 1];

        draggableElement?.addEventListener('mousemove', handleDragEvent, {
            passive: true,
        });

        return () => {
            draggableElement?.removeEventListener('mousemove', handleDragEvent);
        };
    }, [handleDragEvent, dataClass]);

    useEffect(() => {
        const draggableElements = document.querySelectorAll(
            `div[data-class="${dataClass}"]`,
        );
        const draggableElement =
            draggableElements?.[draggableElements?.length - 1];

        draggableElement?.addEventListener('mousedown', handleToggleDragEvent);
        window.addEventListener('mouseup', handleToggleDragEvent);
        draggableElement?.addEventListener('click', handleToggleDragEvent);
        draggableElement?.addEventListener('mouseleave', handleToggleDragEvent);

        return () => {
            draggableElement?.removeEventListener(
                'mousedown',
                handleToggleDragEvent,
            );
            window.removeEventListener('mouseup', handleToggleDragEvent);
            draggableElement?.removeEventListener(
                'click',
                handleToggleDragEvent,
                true,
            );
            draggableElement?.removeEventListener(
                'mouseleave',
                handleToggleDragEvent,
            );
        };
    }, [dataClass]);

    useEffect(() => {
        if (oldX === 0 && oldY === 0) {
            const draggableElements = document.querySelectorAll(
                `div[data-class="${dataClass}"]`,
            );
            const draggableElement =
                draggableElements?.[draggableElements?.length - 1];

            setTimeout(() => {
                draggableElement.classList.remove('epgContainerDisabled');
                isDraggedRef.current = false;
            }, 50);
        }
    }, [oldX, oldY, dataClass]);
}

export default useEpgDragHandlers;
